import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import {
  initTheme,
  setThemeMode as applyThemeMode,
  getStoredThemeMode,
  resolveTheme,
  subscribeToSystemTheme,
} from "../utils/theme";

// Shape: { mode, theme, isDark, setMode, toggle }
//   mode    -> user's preference: "light" | "dark" | "system"
//   theme   -> effective theme currently applied: "light" | "dark"
//   isDark  -> convenience boolean
//   setMode -> set an explicit mode ("light" | "dark" | "system")
//   toggle  -> flip between light and dark (becomes an explicit choice)
const ThemeContext = createContext({
  mode: "light",
  theme: "light",
  isDark: false,
  setMode: () => {},
  toggle: () => {},
});

export const ThemeProvider = ({ children }) => {
  const [{ mode, theme }, setState] = useState(() => {
    // initTheme applies the correct attribute to <html> and returns state.
    if (typeof window === "undefined") {
      return { mode: "light", theme: "light" };
    }
    const { mode: m, effective } = initTheme();
    return { mode: m, theme: effective };
  });

  // Follow OS changes while in "system" mode.
  useEffect(() => {
    const unsub = subscribeToSystemTheme((effective) => {
      setState((prev) => ({ ...prev, theme: effective }));
    });
    return unsub;
  }, []);

  // Keep the mobile browser/PWA chrome dark in both app themes so the native
  // status and navigation icons remain visible on every device.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
      meta.setAttribute("content", "#0b1120");
    }
    const statusBar = document.querySelector(
      'meta[name="apple-mobile-web-app-status-bar-style"]',
    );
    if (statusBar) {
      statusBar.setAttribute("content", "black");
    }
  }, [theme]);

  const setMode = useCallback((nextMode) => {
    const effective = applyThemeMode(nextMode);
    setState({ mode: getStoredThemeMode(), theme: effective });
  }, []);

  const toggle = useCallback(() => {
    setState((prev) => {
      const nextMode = prev.theme === "dark" ? "light" : "dark";
      const effective = applyThemeMode(nextMode);
      return { mode: nextMode, theme: effective };
    });
  }, []);

  const value = {
    mode,
    theme,
    isDark: theme === "dark",
    setMode,
    toggle,
  };

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
